import { useCallback, useEffect, useRef, useState } from 'react'
import { Animated, Easing, Keyboard, Platform } from 'react-native'
import { useReducedMotion } from 'react-native-reanimated'
import { isCoarsePointerWeb } from '../lib/platform'

export const CHAT_COMPOSER_EXPAND_MS = 250

export function useChatComposerExpansion(enabled: boolean) {
  const [expanded, setExpanded] = useState(false)
  const focused = useRef(false)
  const progress = useRef(new Animated.Value(0)).current
  const reducedMotion = useReducedMotion()
  const duration = reducedMotion || process.env.EXPO_PUBLIC_VISUAL_FIXTURES === '1' ? 0 : CHAT_COMPOSER_EXPAND_MS
  const onFocus = useCallback(() => {
    focused.current = true
    setExpanded(enabled)
  }, [enabled])
  const onBlur = useCallback(() => {
    focused.current = false
    setExpanded(false)
  }, [])
  useEffect(() => {
    if (!enabled) { setExpanded(false); return }
    if (Platform.OS === 'web') {
      if (typeof window === 'undefined' || !isCoarsePointerWeb()) return
      const viewport = window.visualViewport
      if (!viewport) return
      let previousHeight = viewport.height
      let restingHeight = viewport.height
      let keyboardSeen = false
      const resize = () => {
        const height = viewport.height
        if (!focused.current) restingHeight = Math.max(restingHeight, height)
        if (focused.current && restingHeight - height > 80) keyboardSeen = true
        // A growing viewport is the browser's keyboard dismissal beginning.
        if (keyboardSeen && height - previousHeight > 4) {
          setExpanded(false)
          keyboardSeen = false
        }
        if (focused.current && restingHeight - height > 80 && previousHeight - height > 4) setExpanded(true)
        previousHeight = height
      }
      viewport.addEventListener('resize', resize)
      return () => viewport.removeEventListener('resize', resize)
    }
    const hide = Keyboard.addListener(Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide', () => setExpanded(false))
    const show = Keyboard.addListener(Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow', () => {
      if (focused.current) setExpanded(true)
    })
    return () => { hide.remove(); show.remove() }
  }, [enabled])
  useEffect(() => {
    // Web accessories use CSS transitions; RN-web Animated can detach its
    // style updates during Strict Mode's effect replay in the dev server.
    if (Platform.OS === 'web') return
    const animation = Animated.timing(progress, {
      toValue: expanded ? 1 : 0,
      duration,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver: false,
    })
    animation.start()
    return () => animation.stop()
  }, [expanded, progress, duration])
  return { expanded, progress, duration, onFocus, onBlur }
}
